import { isNonAdminSalesRep } from "./isNonAdminSalesRep";

// Helpers for the provider-side "Received Contracts" inbox — contracts the
// manufacturer pushes onto this org's `customContracts` (e.g. the PSA that the
// signup flow auto-pushes for the pinned variant + version). getOrgInfo returns
// the whole DoctorOffice vertex, so `auth.orgInfo.customContracts` is already
// available — these pure reads let the Layout reminder and the Settings badge
// share one definition of "what's waiting to be signed" without an extra fetch.

// Mirrors CONTRACT_STATUS.SENT in services/contractApi (an unsigned, delivered
// contract is "sent"; once signed it becomes "signed"). Kept as a literal
// rather than imported so this module stays free of the api.js dependency
// chain — contractApi → api.js → utils.js pulls in ESM-only `marked`, which
// Jest can't transform and would make this helper (and its unit test)
// unloadable.
const CONTRACT_STATUS_SENT = "sent";
// Mirrors TEMPLATE_TYPES.PA ("pa") in services/templateApi — same literal-vs-import
// reasoning as above.
const TEMPLATE_TYPE_PA = "pa";
// Mirrors TEMPLATE_TYPES.NAF_SCHEMA ("naf-schema") — same literal-vs-import reasoning.
const TEMPLATE_TYPE_NAF_SCHEMA = "naf-schema";

// A naf-schema contract is a dead-end when its snapshot has no renderable
// sections (empty []/{}/missing). ReceivedContractsList routes naf-schema
// contracts to ModalNewAccountForm, which only renders its form — including the
// sign AND "Back to contracts" controls — when content.sections is a non-empty
// array (see ModalNewAccountForm.js `contractSections`/`showForm`). An empty one
// therefore can be neither signed nor dismissed, yet stays status "sent" → it
// would re-pop the login reminder on every session forever. Exclude it from the
// unsigned set so a malformed/stale contract stops nagging.
//
// Scoped to naf-schema ONLY: every other templateType routes to
// ModalContractSign, which always renders live Cancel + Sign controls (so it's
// signable, never a trap) and MUST keep counting — hiding a genuinely-pending
// contract would be a compliance miss.
//
// Exported so ReceivedContractsList can share the exact same rule for its
// in-page count + the "Review & Sign" affordance (its list rows come from a
// different source, useContracts, but for received scope it's the same
// customContracts data, so the same predicate applies).
export function isUnsignableNafContract(c) {
  if (!c || c.templateType !== TEMPLATE_TYPE_NAF_SCHEMA) return false;
  const sections = c.content && c.content.sections;
  return !(Array.isArray(sections) && sections.length > 0);
}

export function getReceivedContracts(orgInfo) {
  const arr = orgInfo?.customContracts;
  return Array.isArray(arr) ? arr.filter(Boolean) : [];
}

export function unsignedReceivedContracts(orgInfo) {
  return getReceivedContracts(orgInfo).filter((c) => c.status === CONTRACT_STATUS_SENT && !isUnsignableNafContract(c));
}

export function hasUnsignedReceivedContracts(orgInfo) {
  return unsignedReceivedContracts(orgInfo).length > 0;
}

// True when a PSA contract has been received (signed or not). Used to suppress
// the legacy standalone-PSA reminder once the manufacturer's auto-pushed PSA
// contract is the real signing surface, so the two don't compete / misdirect.
export function hasReceivedPsaContract(orgInfo) {
  return getReceivedContracts(orgInfo).some((c) => c.templateType === TEMPLATE_TYPE_PA);
}

// Who may sign org-level contracts: the provider org's own account holder or a
// supervisor; sales reps never sign org docs. Single definition shared by the
// Layout PSA reminder and the received-contracts reminder so the two gates
// can't drift. (Settings' canAccessPsa intentionally differs — it also allows
// ultimate orgs and additionally gates on requirePsa/dmeEnabled — so it is NOT
// folded in here.)
export function canSignOrgContracts(auth, hasGroup) {
  const isSupervisor = typeof hasGroup === "function" && hasGroup("supervisor");
  const isProviderPlan = auth?.session?.currentOrganizationPlan === "order-provider";
  return isSupervisor || (isProviderPlan && !isNonAdminSalesRep(auth));
}
